import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Container, Row, Col, Card, Badge, Table, Button, Spinner, Alert, Form, ProgressBar, Tabs, Tab } from 'react-bootstrap';
import { toast } from 'react-toastify';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { BarChart, LineChart, PieChart, FunnelChart } from 'echarts/charts';
import {
  GridComponent, TooltipComponent, TitleComponent, LegendComponent, DataZoomComponent, MarkLineComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import budgetService from '../../../services/budgetService';

echarts.use([BarChart, LineChart, PieChart, FunnelChart, GridComponent, TooltipComponent, TitleComponent, LegendComponent, DataZoomComponent, MarkLineComponent, CanvasRenderer]);

const fmt = (n) => n == null ? '0' : Number(n).toLocaleString('es-EC');
const fmtC = (n) => n == null ? '$0' : `$${Number(n).toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtP = (n) => n == null ? '0%' : `${(Number(n) * 100).toFixed(1)}%`;

const FORD_BLUE = '#003478';
const MONTH_KEYS = ['abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic', 'ene', 'feb', 'mar'];
const MONTH_LABELS = ['Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic', 'Ene', 'Feb', 'Mar'];

const MODEL_COLORS = {
  RANGER: '#1B3A7B', TERRITORY: '#00A3E0', ESCAPE: '#F26522',
  F150: '#D0021B', EXPLORER: '#4A90D9', BRONCO: '#7ED321',
  EVEREST: '#8B572A', EXPEDITION: '#9B59B6', MAVERICK: '#2ECC71',
  GENERAL: '#9B9B9B', OTRO: '#B8B8B8',
};
const getModelColor = (m) => MODEL_COLORS[m?.toUpperCase()] || '#6c757d';

const KpiCard = ({ title, value, subtitle, icon, color = FORD_BLUE }) => (
  <Card className="border-0 shadow-sm h-100">
    <Card.Body className="p-3">
      <div className="d-flex align-items-center justify-content-between">
        <div>
          <div className="text-muted small mb-1">{title}</div>
          <div className="fw-bold fs-4" style={{ color }}>{value}</div>
          {subtitle && <div className="text-muted small mt-1">{subtitle}</div>}
        </div>
        {icon && <div style={{ color, opacity: 0.3, fontSize: '2rem' }}><i className={`fas fa-${icon}`} /></div>}
      </div>
    </Card.Body>
  </Card>
);

const SortableTable = ({ columns, data, maxHeight = '500px' }) => {
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('desc');
  const [colFilters, setColFilters] = useState({});
  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  };
  const filtered = useMemo(() => {
    let d = [...(data || [])];
    Object.entries(colFilters).forEach(([key, val]) => {
      if (val) d = d.filter(r => String(r[key] ?? '').toLowerCase().includes(val.toLowerCase()));
    });
    if (sortCol) {
      d.sort((a, b) => {
        const na = Number(a[sortCol]), nb = Number(b[sortCol]);
        if (!isNaN(na) && !isNaN(nb)) return sortDir === 'asc' ? na - nb : nb - na;
        return sortDir === 'asc' ? String(a[sortCol] || '').localeCompare(String(b[sortCol] || '')) : String(b[sortCol] || '').localeCompare(String(a[sortCol] || ''));
      });
    }
    return d;
  }, [data, sortCol, sortDir, colFilters]);
  return (
    <div style={{ maxHeight, overflowY: 'auto' }}>
      <Table size="sm" hover striped className="mb-0">
        <thead className="sticky-top bg-white">
          <tr>{columns.map(c => <th key={c.key} className="small" style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => handleSort(c.key)}>{c.label} {sortCol === c.key ? (sortDir === 'asc' ? '▲' : '▼') : ''}</th>)}</tr>
          <tr>{columns.map(c => <th key={c.key} className="p-0"><Form.Control size="sm" placeholder="Filtrar..." value={colFilters[c.key] || ''} className="border-0 rounded-0" style={{ fontSize: '0.7rem' }} onChange={e => setColFilters(prev => ({ ...prev, [c.key]: e.target.value }))} /></th>)}</tr>
        </thead>
        <tbody>
          {filtered.map((row, i) => <tr key={i}>{columns.map(c => <td key={c.key} className="small" style={{ whiteSpace: 'nowrap' }}>{c.render ? c.render(row[c.key], row) : (row[c.key] ?? '-')}</td>)}</tr>)}
          {filtered.length === 0 && <tr><td colSpan={columns.length} className="text-center text-muted py-3">Sin datos</td></tr>}
        </tbody>
      </Table>
    </div>
  );
};

const FordSalesDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [dashData, setDashData] = useState(null);
  const [execReport, setExecReport] = useState(null);
  const [execLoading, setExecLoading] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [activeTab, setActiveTab] = useState('resumen');
  const [filterModelo, setFilterModelo] = useState('');
  const [filterAgencia, setFilterAgencia] = useState('');
  const [filterTipoVenta, setFilterTipoVenta] = useState('');
  const [uploadPeriodo, setUploadPeriodo] = useState('');
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  const loadPeriods = useCallback(async () => { const res = await budgetService.getFordSalesPeriods(); if (res.success) setPeriods(res.data || []); }, []);
  const loadUploads = useCallback(async () => { const res = await budgetService.getFordSalesUploads(); if (res.success) setUploads(res.data || []); }, []);

  const loadDashboard = useCallback(async (periodo) => {
    setLoading(true);
    const res = await budgetService.getFordSalesDashboard({ periodoReporte: periodo, modelo: filterModelo || undefined, agencia: filterAgencia || undefined, tipoVenta: filterTipoVenta || undefined });
    if (res.success) { setDashData(res.data); if (!periodo && res.data?.periodo) setSelectedPeriod(res.data.periodo); }
    setLoading(false);
  }, [filterModelo, filterAgencia, filterTipoVenta]);

  const loadExecReport = useCallback(async (periodo) => {
    if (!periodo) return;
    setExecLoading(true);
    const res = await budgetService.getFordSalesExecutiveReport(periodo);
    if (res.success) setExecReport(res.data); else toast.error(res.error);
    setExecLoading(false);
  }, []);

  useEffect(() => { loadPeriods(); loadUploads(); loadDashboard(''); }, []); // eslint-disable-line
  useEffect(() => { if (selectedPeriod) loadDashboard(selectedPeriod); }, [selectedPeriod, filterModelo, filterAgencia, filterTipoVenta]); // eslint-disable-line
  useEffect(() => { if (activeTab === 'gerencial' && selectedPeriod && !execReport) loadExecReport(selectedPeriod); }, [activeTab, selectedPeriod]); // eslint-disable-line

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!uploadPeriodo || !/^\d{4}-\d{2}$/.test(uploadPeriodo)) { toast.warning('Selecciona el período antes de subir el archivo'); return; }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    const res = await budgetService.uploadFordSalesFile(formData, uploadPeriodo);
    if (res.success && res.data?.jobId) {
      setUploadProgress({ status: 'parsing', progress: 5, message: 'Parseando archivo...' });
      pollRef.current = setInterval(async () => {
        const st = await budgetService.getFordSalesUploadStatus(res.data.jobId);
        if (st.success && st.data) {
          setUploadProgress(st.data);
          if (st.data.status === 'completed' || st.data.status === 'error') {
            clearInterval(pollRef.current);
            setTimeout(() => { setUploading(false); setUploadProgress(null); loadPeriods(); loadUploads(); setSelectedPeriod(uploadPeriodo); loadDashboard(uploadPeriodo); setExecReport(null); }, 2000);
            if (st.data.status === 'completed') toast.success(st.data.message); else toast.error(st.data.error || st.data.message);
          }
        }
      }, 2000);
    } else { toast.error(res.error); setUploading(false); }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDeleteUpload = async (id) => {
    if (!window.confirm('¿Eliminar este upload y todos sus datos?')) return;
    const res = await budgetService.deleteFordSalesUpload(id);
    if (res.success) { toast.success(res.message); loadPeriods(); loadUploads(); loadDashboard(selectedPeriod); } else toast.error(res.error);
  };

  const generateCurrentPeriodOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('es-EC', { month: 'long', year: 'numeric' });
      options.push({ value: val, label: label.charAt(0).toUpperCase() + label.slice(1) });
    }
    return options;
  };

  const kpis = dashData?.kpis;

  // ===== CHART: 12-month trend by agencia =====
  const trendAgenciaOpt = useMemo(() => {
    const d = dashData?.ventasPorAgencia || [];
    if (!d.length) return {};
    const total = d.find(r => r.agencia === 'Total general');
    if (!total) return {};
    return {
      tooltip: { trigger: 'axis' },
      legend: { show: false },
      grid: { left: '3%', right: '3%', bottom: '3%', containLabel: true },
      xAxis: { type: 'category', data: MONTH_LABELS },
      yAxis: { type: 'value', name: 'Ventas' },
      series: [{
        name: 'Ventas', type: 'bar', data: MONTH_KEYS.map(k => total.ventas?.[k] ?? 0),
        itemStyle: { color: FORD_BLUE },
        label: { show: true, position: 'top', fontSize: 10 },
      }, {
        name: 'Tendencia', type: 'line',
        data: MONTH_KEYS.map(k => total.ventas?.[k] ?? 0),
        itemStyle: { color: '#F26522' }, lineStyle: { width: 2 }, smooth: true,
      }],
    };
  }, [dashData]);

  // ===== CHART: Ventas mes actual por modelo (from modeloAgenciaMatriz) =====
  const modeloMesOpt = useMemo(() => {
    const d = (dashData?.modeloAgenciaMatriz || []).filter(r => r.modelo !== 'Total general');
    if (!d.length) return {};
    const sorted = [...d].sort((a, b) => (b.agencias?.Total || 0) - (a.agencias?.Total || 0));
    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      grid: { left: '25%', right: '5%', bottom: '3%', containLabel: true },
      yAxis: { type: 'category', data: sorted.map(r => r.modelo?.substring(0, 30)).reverse() },
      xAxis: { type: 'value', name: 'Ventas' },
      series: [{
        name: 'Ventas Mes', type: 'bar',
        data: sorted.map(r => r.agencias?.Total || 0).reverse(),
        label: { show: true, position: 'right', fontSize: 10 },
        itemStyle: { color: FORD_BLUE },
      }],
    };
  }, [dashData]);

  // ===== CHART: Funnel CRM =====
  const funnelOpt = useMemo(() => {
    if (!kpis) return {};
    const maxVal = Math.max(kpis.totalLeads, 1);
    const norm = (v) => Math.max(Math.round((v / maxVal) * 100), 1);
    const pct = (a, b) => b > 0 ? ((a / b) * 100).toFixed(1) + '%' : '-';
    const tasaCierreVal = kpis.totalLeads > 0 ? kpis.ventasMesActual / kpis.totalLeads : 0;
    const steps = [
      { name: 'Leads', val: kpis.totalLeads, color: '#B8D4E3', prev: null },
      { name: 'Gestionados', val: kpis.leadsGestionados, color: '#6BAED6', prev: kpis.totalLeads },
      { name: 'Citas', val: kpis.totalCitas, color: '#2171B5', prev: kpis.leadsGestionados },
      { name: 'Prospección', val: kpis.totalProspeccion, color: '#08519C', prev: kpis.totalCitas },
      { name: '% Tasa de Cierre', val: kpis.ventasMesActual, color: '#D0021B', prev: kpis.totalProspeccion, isTasa: true, tasaVal: tasaCierreVal },
      { name: 'Ventas', val: kpis.ventasMesActual, color: FORD_BLUE, prev: null },
    ];
    return {
      tooltip: {
        trigger: 'item',
        formatter: (p) => {
          const s = steps[p.dataIndex];
          if (s.isTasa) return `<strong>${s.name}</strong><br/>${(s.tasaVal * 100).toFixed(1)}%<br/>${kpis.ventasMesActual} ventas / ${kpis.totalLeads} leads`;
          let html = `<strong>${s.name}</strong>: ${Number(s.val).toLocaleString()}`;
          if (s.prev != null) html += `<br/>Conversión: ${pct(s.val, s.prev)}`;
          return html;
        },
      },
      series: [{
        type: 'funnel', left: '10%', width: '80%', gap: 2, sort: 'descending',
        label: {
          show: true, position: 'inside', fontSize: 10,
          formatter: (p) => {
            const s = steps[p.dataIndex];
            if (s.isTasa) return `${s.name}\n${(s.tasaVal * 100).toFixed(1)}%`;
            const rate = s.prev != null ? ` (${pct(s.val, s.prev)})` : '';
            return `${s.name}\n${Number(s.val).toLocaleString()}${rate}`;
          },
        },
        data: steps.map(s => ({ name: s.name, value: norm(s.val), realValue: s.val, itemStyle: { color: s.color } })),
      }],
    };
  }, [kpis]);

  // ===== CHART: Resumen Total bar (ventas por agencia mes) =====
  const resumenTotalOpt = useMemo(() => {
    const d = (dashData?.resumenTotal || []).filter(r => r.agencia !== 'Total');
    if (!d.length) return {};
    const sorted = [...d].sort((a, b) => b.ventasFecha - a.ventasFecha);
    return {
      tooltip: {
        trigger: 'axis', axisPointer: { type: 'shadow' },
        formatter: (params) => {
          const p = params[0]; const row = sorted[sorted.length - 1 - p.dataIndex];
          return `<strong>${row?.agencia}</strong><br/>Ventas: ${row?.ventasFecha}<br/>BP: ${row?.bpMes}<br/>Cumpl: ${fmtP(row?.cumplimiento)}`;
        }
      },
      grid: { left: '20%', right: '10%', bottom: '3%', containLabel: true },
      yAxis: { type: 'category', data: sorted.map(r => r.agencia).reverse() },
      xAxis: { type: 'value' },
      series: [
        { name: 'Ventas', type: 'bar', data: sorted.map(r => r.ventasFecha).reverse(), itemStyle: { color: FORD_BLUE }, label: { show: true, position: 'right', fontSize: 10 } },
        { name: 'BP', type: 'bar', data: sorted.map(r => r.bpMes).reverse(), itemStyle: { color: '#E0E0E0' } },
      ],
      legend: { data: ['Ventas', 'BP'] },
    };
  }, [dashData]);

  // ===== CHART: Stock por modelo =====
  const stockOpt = useMemo(() => {
    const d = (dashData?.stockInventario || []).filter(r => r.modelo !== 'Total general');
    if (!d.length) return {};
    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      grid: { left: '25%', right: '5%', bottom: '3%', containLabel: true },
      legend: { data: ['Stock', 'Tránsito'] },
      yAxis: { type: 'category', data: d.map(r => r.modelo?.substring(0, 35)) },
      xAxis: { type: 'value' },
      series: [
        { name: 'Stock', type: 'bar', stack: 'inv', data: d.map(r => r.stock), itemStyle: { color: FORD_BLUE } },
        { name: 'Tránsito', type: 'bar', stack: 'inv', data: d.map(r => r.transito), itemStyle: { color: '#00A3E0' } },
      ],
    };
  }, [dashData]);

  // ===== CHART: 12 month stacked by modelo agrupado =====
  const modeloHistOpt = useMemo(() => {
    const d = (dashData?.ventasPorModeloAgrupado || []).filter(r => r.modelo !== 'Total general');
    if (!d.length) return {};
    const familyMap = {};
    d.forEach(m => {
      let fam = 'OTRO';
      const raw = m.modelo?.toUpperCase() || '';
      if (raw.includes('TERRITORY')) fam = 'TERRITORY';
      else if (raw.includes('ESCAPE')) fam = 'ESCAPE';
      else if (raw.includes('RANGER')) fam = 'RANGER';
      else if (raw.includes('F150') || raw.includes('F-150') || raw.includes('F 150')) fam = 'F150';
      else if (raw.includes('EXPLORER')) fam = 'EXPLORER';
      else if (raw.includes('BRONCO')) fam = 'BRONCO';
      else if (raw.includes('EVEREST')) fam = 'EVEREST';
      else if (raw.includes('EXPEDITION')) fam = 'EXPEDITION';
      else if (raw.includes('MAVERICK')) fam = 'MAVERICK';
      if (!familyMap[fam]) familyMap[fam] = {};
      MONTH_KEYS.forEach(k => { familyMap[fam][k] = (familyMap[fam][k] || 0) + (m.ventas?.[k] || 0); });
    });
    const families = Object.keys(familyMap).sort();
    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      legend: { data: families, type: 'scroll' },
      grid: { left: '3%', right: '3%', bottom: '3%', containLabel: true },
      xAxis: { type: 'category', data: MONTH_LABELS },
      yAxis: { type: 'value' },
      series: families.map(fam => ({
        name: fam, type: 'bar', stack: 'total',
        data: MONTH_KEYS.map(k => familyMap[fam][k] || 0),
        itemStyle: { color: getModelColor(fam) },
      })),
    };
  }, [dashData]);

  // ===== EXEC REPORT CHARTS =====
  const execFunnelOpt = useMemo(() => {
    if (!execReport?.funnel) return {};
    const f = execReport.funnel;
    const maxVal = Math.max(f.impresiones, 1);
    const norm = (v) => Math.max(Math.round((v / maxVal) * 100), 1);
    return {
      tooltip: { trigger: 'item', formatter: (p) => `<strong>${p.name}</strong><br/>${Number(p.data?.realValue || 0).toLocaleString()}` },
      series: [{
        type: 'funnel', left: '10%', width: '80%', gap: 2, sort: 'descending',
        label: { show: true, position: 'inside', fontSize: 10, formatter: (p) => `${p.name}\n${Number(p.data?.realValue || 0).toLocaleString()}` },
        data: [
          { name: 'Impresiones', value: norm(f.impresiones), realValue: f.impresiones, itemStyle: { color: '#B8D4E3' } },
          { name: 'Clicks', value: norm(f.clicks), realValue: f.clicks, itemStyle: { color: '#6BAED6' } },
          { name: 'Leads Digital', value: norm(f.leadsDigital), realValue: Math.round(f.leadsDigital), itemStyle: { color: '#2171B5' } },
          { name: 'Leads CRM', value: norm(f.leadsCRM), realValue: f.leadsCRM, itemStyle: { color: '#08519C' } },
          { name: 'Ventas', value: norm(f.ventasNetas), realValue: f.ventasNetas, itemStyle: { color: FORD_BLUE } },
        ],
      }],
    };
  }, [execReport]);

  const execModeloCrossOpt = useMemo(() => {
    if (!execReport?.byModelo) return {};
    const d = execReport.byModelo.filter(r => r.modelo !== 'GENERAL' && r.modelo !== 'OTRO');
    return {
      tooltip: {
        trigger: 'axis',
        formatter: (params) => {
          const idx = params[0]?.dataIndex; const row = d[idx];
          return `<strong>${row?.modelo}</strong><br/>Ventas: ${row?.ventas || 0}<br/>Leads: ${Math.round(row?.leadsDigital || 0)}<br/>Inversión: $${Number(row?.inversionDigital || 0).toFixed(2)}<br/>CPL: $${Number(row?.cpl || 0).toFixed(2)}<br/>Costo/Venta: $${Number(row?.costoVenta || 0).toFixed(2)}`;
        },
      },
      legend: { data: ['Ventas', 'Leads Digital', 'Inversión ($)'] },
      grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
      xAxis: { type: 'category', data: d.map(r => r.modelo), axisLabel: { rotate: 30 } },
      yAxis: [{ type: 'value', name: 'Cantidad' }, { type: 'value', name: 'Inversión $' }],
      series: [
        { name: 'Ventas', type: 'bar', data: d.map(r => r.ventas), itemStyle: { color: FORD_BLUE }, label: { show: true, position: 'top', fontSize: 10 } },
        { name: 'Leads Digital', type: 'bar', data: d.map(r => Math.round(r.leadsDigital || 0)), itemStyle: { color: '#00A3E0' }, label: { show: true, position: 'top', fontSize: 10 } },
        { name: 'Inversión ($)', type: 'line', yAxisIndex: 1, data: d.map(r => Number(r.inversionDigital || 0).toFixed(2)), itemStyle: { color: '#F26522' }, smooth: true },
      ],
    };
  }, [execReport]);

  const execDailyOpt = useMemo(() => {
    if (!execReport?.dailyCross) return {};
    const d = execReport.dailyCross;
    return {
      tooltip: { trigger: 'axis' },
      legend: { data: ['Leads Digital', 'Inversión'] },
      grid: { left: '3%', right: '4%', bottom: '15%', containLabel: true },
      dataZoom: [{ type: 'slider', start: 0, end: 100 }],
      xAxis: { type: 'category', data: d.map(r => r.dia) },
      yAxis: [{ type: 'value', name: 'Cantidad' }, { type: 'value', name: 'Inversión $' }],
      series: [
        { name: 'Leads Digital', type: 'bar', data: d.map(r => Math.round(r.leadsDigital)), itemStyle: { color: '#00A3E0' } },
        { name: 'Inversión', type: 'line', yAxisIndex: 1, data: d.map(r => r.inversionDigital?.toFixed(2)), itemStyle: { color: '#F26522' }, smooth: true },
      ],
    };
  }, [execReport]);

  return (
    <Container fluid className="py-3">
      {/* HEADER */}
      <Row className="mb-3 align-items-center">
        <Col>
          <h4 className="mb-0" style={{ color: FORD_BLUE }}><i className="fas fa-car me-2" />Ford Ecuador — Dashboard de Ventas</h4>
          <small className="text-muted">Resumen de ventas, funnel CRM y cruce con métricas digitales</small>
        </Col>
        <Col xs="auto" className="d-flex gap-2 align-items-center">
          <Form.Select size="sm" style={{ width: '180px' }} value={selectedPeriod} onChange={e => { setSelectedPeriod(e.target.value); setExecReport(null); }}>
            <option value="">Último período</option>
            {periods.map(p => <option key={p.periodo} value={p.periodo}>{p.periodo} ({fmt(p.totalVentasNetas)} ventas)</option>)}
          </Form.Select>
        </Col>
      </Row>

      {/* UPLOAD */}
      <Card className="border-0 shadow-sm mb-3">
        <Card.Body className="py-2 px-3">
          <Row className="align-items-center">
            <Col xs="auto"><i className="fas fa-cloud-upload-alt text-primary me-2" /><strong className="small">Subir Excel de Ventas</strong></Col>
            <Col xs="auto">
              <Form.Select size="sm" style={{ width: '160px' }} value={uploadPeriodo} onChange={e => setUploadPeriodo(e.target.value)}>
                <option value="">Período...</option>
                {generateCurrentPeriodOptions().map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </Form.Select>
            </Col>
            <Col xs="auto">
              <Button size="sm" variant="primary" disabled={uploading || !uploadPeriodo} onClick={() => fileInputRef.current?.click()}>
                {uploading ? <Spinner size="sm" className="me-1" /> : <i className="fas fa-upload me-1" />}
                {uploading ? 'Procesando...' : 'Seleccionar archivo'}
              </Button>
              <input type="file" ref={fileInputRef} accept=".xlsx,.xls" className="d-none" onChange={handleFileUpload} />
            </Col>
            {uploadProgress && (
              <Col>
                <ProgressBar now={uploadProgress.progress} label={`${uploadProgress.progress}%`}
                  variant={uploadProgress.status === 'error' ? 'danger' : uploadProgress.status === 'completed' ? 'success' : 'primary'}
                  animated={uploadProgress.status !== 'completed' && uploadProgress.status !== 'error'} />
                <small className="text-muted">{uploadProgress.message}</small>
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>

      {loading && !dashData ? (
        <div className="text-center py-5"><Spinner /><p className="mt-2 text-muted">Cargando datos...</p></div>
      ) : !dashData?.kpis ? (
        <Alert variant="info">No hay datos de ventas Ford para el período seleccionado. Sube un Excel para comenzar.</Alert>
      ) : (
        <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-3">
          {/* ==================== TAB: RESUMEN ==================== */}
          <Tab eventKey="resumen" title={<><i className="fas fa-chart-line me-1" />Resumen</>}>
            <Row className="g-3 mb-3">
              <Col md={2}><KpiCard title="Ventas del Mes" value={fmt(kpis.ventasMesActual)} subtitle={`BP: ${fmt(kpis.bpMes)}`} icon="shopping-cart" /></Col>
              <Col md={2}><KpiCard title="Cumplimiento BP" value={fmtP(kpis.cumplimientoBP)} subtitle={`${fmt(kpis.ventasMesActual)} / ${fmt(kpis.bpMes)}`} icon="bullseye" color={kpis.cumplimientoBP >= 0.8 ? '#28a745' : kpis.cumplimientoBP >= 0.5 ? '#F26522' : '#D0021B'} /></Col>
              <Col md={2}><KpiCard title="Leads CRM" value={fmt(kpis.totalLeads)} subtitle={`${fmtP(kpis.tasaGestion)} gestionados`} icon="user-plus" color="#00A3E0" /></Col>
              <Col md={2}><KpiCard title="Citas" value={fmt(kpis.totalCitas)} subtitle={`${fmtP(kpis.tasaCita)} tasa cita`} icon="calendar-check" color="#2171B5" /></Col>
              <Col md={2}><KpiCard title="ShowUp / Test Drive" value={`${fmt(kpis.totalShowUp)} / ${fmt(kpis.totalTestDrive)}`} icon="car-side" color="#7ED321" /></Col>
              <Col md={2}><KpiCard title="Tasa de Cierre" value={fmtP(kpis.tasaCierre)} subtitle={`Prosp: ${fmt(kpis.totalProspeccion)}`} icon="percentage" color="#F26522" /></Col>
            </Row>

            <Row className="g-3 mb-3">
              <Col md={8}>
                <Card className="border-0 shadow-sm">
                  <Card.Header className="bg-white border-0 py-2"><strong className="small">Tendencia 12 Meses</strong></Card.Header>
                  <Card.Body className="p-2"><ReactEChartsCore echarts={echarts} option={trendAgenciaOpt} style={{ height: '300px' }} /></Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="border-0 shadow-sm">
                  <Card.Header className="bg-white border-0 py-2"><strong className="small">Funnel CRM del Mes</strong></Card.Header>
                  <Card.Body className="p-2"><ReactEChartsCore echarts={echarts} option={funnelOpt} style={{ height: '300px' }} /></Card.Body>
                </Card>
              </Col>
            </Row>

            <Row className="g-3 mb-3">
              <Col md={6}>
                <Card className="border-0 shadow-sm">
                  <Card.Header className="bg-white border-0 py-2"><strong className="small">Ventas del Mes por Modelo</strong></Card.Header>
                  <Card.Body className="p-2"><ReactEChartsCore echarts={echarts} option={modeloMesOpt} style={{ height: '300px' }} /></Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="border-0 shadow-sm">
                  <Card.Header className="bg-white border-0 py-2"><strong className="small">Ventas vs BP por Agencia</strong></Card.Header>
                  <Card.Body className="p-2"><ReactEChartsCore echarts={echarts} option={resumenTotalOpt} style={{ height: '300px' }} /></Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab>

          {/* ==================== TAB: POR MODELO ==================== */}
          <Tab eventKey="modelos" title={<><i className="fas fa-car me-1" />Por Modelo</>}>
            <Row className="g-3 mb-3">
              <Col md={7}>
                <Card className="border-0 shadow-sm">
                  <Card.Header className="bg-white border-0 py-2"><strong className="small">Ventas 12 Meses por Familia</strong></Card.Header>
                  <Card.Body className="p-2"><ReactEChartsCore echarts={echarts} option={modeloHistOpt} style={{ height: '350px' }} /></Card.Body>
                </Card>
              </Col>
              <Col md={5}>
                <Card className="border-0 shadow-sm">
                  <Card.Header className="bg-white border-0 py-2"><strong className="small">Stock / Inventario</strong></Card.Header>
                  <Card.Body className="p-2"><ReactEChartsCore echarts={echarts} option={stockOpt} style={{ height: '350px' }} /></Card.Body>
                </Card>
              </Col>
            </Row>

            <Card className="border-0 shadow-sm mb-3">
              <Card.Header className="bg-white border-0 py-2"><strong className="small">Ventas 12 Meses por Modelo/Versión</strong></Card.Header>
              <Card.Body className="p-2">
                <SortableTable
                  columns={[
                    { key: 'modelo', label: 'Modelo/Versión' },
                    ...MONTH_LABELS.map((l, i) => ({ key: MONTH_KEYS[i], label: l, render: (_, row) => fmt(row.ventas?.[MONTH_KEYS[i]]) })),
                  ]}
                  data={dashData?.ventasPorModelo || []}
                />
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white border-0 py-2"><strong className="small">Stock e Inventario por Modelo</strong></Card.Header>
              <Card.Body className="p-2">
                <SortableTable
                  columns={[
                    { key: 'modelo', label: 'Modelo' },
                    { key: 'stock', label: 'Stock', render: v => fmt(v) },
                    { key: 'transito', label: 'Tránsito', render: v => fmt(v) },
                    { key: 'total', label: 'Total', render: v => <strong>{fmt(v)}</strong> },
                    { key: 'mos', label: 'MOS', render: v => v ? Number(v).toFixed(1) : '-' },
                  ]}
                  data={dashData?.stockInventario || []}
                />
              </Card.Body>
            </Card>
          </Tab>

          {/* ==================== TAB: POR AGENCIA ==================== */}
          <Tab eventKey="agencias" title={<><i className="fas fa-building me-1" />Por Agencia</>}>
            <Card className="border-0 shadow-sm mb-3">
              <Card.Header className="bg-white border-0 py-2"><strong className="small">Ventas 12 Meses por Agencia</strong></Card.Header>
              <Card.Body className="p-2">
                <SortableTable
                  columns={[
                    { key: 'agencia', label: 'Agencia' },
                    ...MONTH_LABELS.map((l, i) => ({ key: MONTH_KEYS[i], label: l, render: (_, row) => fmt(row.ventas?.[MONTH_KEYS[i]]) })),
                  ]}
                  data={dashData?.ventasPorAgencia || []}
                />
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white border-0 py-2"><strong className="small">Resumen del Mes: Ventas vs BP por Agencia</strong></Card.Header>
              <Card.Body className="p-2">
                <SortableTable
                  columns={[
                    { key: 'agencia', label: 'Agencia' },
                    { key: 'ventasFecha', label: 'Ventas', render: v => <strong>{fmt(v)}</strong> },
                    { key: 'bpMes', label: 'BP Mes', render: v => fmt(v) },
                    { key: 'cumplimiento', label: '% Cumpl.', render: v => <Badge bg={v >= 0.8 ? 'success' : v >= 0.5 ? 'warning' : 'danger'}>{fmtP(v)}</Badge> },
                    { key: 'ventasDia', label: 'Vta Día', render: v => fmt(v) },
                    { key: 'objetivoDiario', label: 'Obj. Diario', render: v => fmt(v) },
                  ]}
                  data={dashData?.resumenTotal || []}
                />
              </Card.Body>
            </Card>
          </Tab>

          {/* ==================== TAB: FUNNEL ==================== */}
          <Tab eventKey="funnel" title={<><i className="fas fa-filter me-1" />Funnel CRM</>}>
            <Row className="g-3 mb-3">
              <Col md={2}><KpiCard title="Leads" value={fmt(kpis.totalLeads)} subtitle={`${fmtP(kpis.tasaGestion)} gestionados (${fmt(kpis.leadsGestionados)})`} icon="user-plus" color="#B8D4E3" /></Col>
              <Col md={2}><KpiCard title="Citas" value={fmt(kpis.totalCitas)} subtitle={`${fmtP(kpis.tasaCita)} tasa`} icon="calendar-check" color="#6BAED6" /></Col>
              <Col md={2}><KpiCard title="ShowUp" value={fmt(kpis.totalShowUp)} subtitle={`${fmtP(kpis.tasaShowUp)} tasa`} icon="door-open" color="#2171B5" /></Col>
              <Col md={2}><KpiCard title="Test Drive" value={fmt(kpis.totalTestDrive)} icon="car-side" color="#08519C" /></Col>
              <Col md={2}><KpiCard title="Ventas" value={fmt(kpis.ventasMesActual)} subtitle={`BP: ${fmt(kpis.bpMes)}`} icon="check-circle" color={FORD_BLUE} /></Col>
              <Col md={2}><KpiCard title="% Tasa de Cierre" value={fmtP(kpis.tasaCierre)} subtitle={`${fmt(kpis.ventasMesActual)} ventas / ${fmt(kpis.totalLeads)} leads`} icon="percentage" color="#D0021B" /></Col>
            </Row>

            <Row className="g-3 mb-3">
              <Col md={5}>
                <Card className="border-0 shadow-sm">
                  <Card.Header className="bg-white border-0 py-2"><strong className="small">Embudo de Conversión</strong></Card.Header>
                  <Card.Body className="p-2"><ReactEChartsCore echarts={echarts} option={funnelOpt} style={{ height: '350px' }} /></Card.Body>
                </Card>
              </Col>
              <Col md={7}>
                <Card className="border-0 shadow-sm">
                  <Card.Header className="bg-white border-0 py-2"><strong className="small">Funnel por Agencia</strong></Card.Header>
                  <Card.Body className="p-2">
                    <SortableTable
                      maxHeight="350px"
                      columns={[
                        { key: 'agencia', label: 'Agencia' },
                        { key: 'leads', label: 'Leads', render: v => fmt(v) },
                        { key: 'gestionados', label: 'Gestion.', render: v => fmt(v) },
                        { key: 'citas', label: 'Citas', render: v => fmt(v) },
                        { key: 'tasaCita', label: '% Cita', render: v => fmtP(v) },
                        { key: 'showUp', label: 'ShowUp', render: v => fmt(v) },
                        { key: 'prospeccion', label: 'Prosp.', render: v => fmt(v) },
                        { key: 'ventas', label: 'Ventas', render: v => <strong>{fmt(v)}</strong> },
                        { key: 'tasaCierre', label: '% Tasa de Cierre', render: v => <Badge bg={v >= 0.2 ? 'success' : v >= 0.1 ? 'warning' : 'danger'}>{fmtP(v)}</Badge> },
                        { key: 'testDrive', label: 'TD', render: v => fmt(v) },
                        { key: 'bpAgencia', label: 'BP', render: v => fmt(v) },
                        { key: 'cumplimiento', label: '% Cumpl', render: v => <Badge bg={v >= 0.8 ? 'success' : v >= 0.5 ? 'warning' : 'danger'}>{fmtP(v)}</Badge> },
                      ]}
                      data={dashData?.funnelPorAgencia || []}
                    />
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab>

          {/* ==================== TAB: DETALLE ==================== */}
          <Tab eventKey="detalle" title={<><i className="fas fa-table me-1" />Detalle Facturas</>}>
            {/* Filters */}
            <Row className="mb-3">
              <Col xs="auto"><Form.Select size="sm" value={filterModelo} onChange={e => setFilterModelo(e.target.value)}><option value="">Todos los modelos</option>{(dashData?.filters?.modelos || []).map(m => <option key={m.modelo} value={m.modelo}>{m.modelo}</option>)}</Form.Select></Col>
              <Col xs="auto"><Form.Select size="sm" value={filterAgencia} onChange={e => setFilterAgencia(e.target.value)}><option value="">Todas las agencias</option>{(dashData?.filters?.agencias || []).map(a => <option key={a.agencia} value={a.agencia}>{a.agencia}</option>)}</Form.Select></Col>
              <Col xs="auto"><Form.Select size="sm" value={filterTipoVenta} onChange={e => setFilterTipoVenta(e.target.value)}><option value="">Tipo de venta</option>{(dashData?.filters?.tiposVenta || []).map(t => <option key={t.tipoVenta} value={t.tipoVenta}>{t.tipoVenta}</option>)}</Form.Select></Col>
              {(filterModelo || filterAgencia || filterTipoVenta) && <Col xs="auto"><Button size="sm" variant="outline-secondary" onClick={() => { setFilterModelo(''); setFilterAgencia(''); setFilterTipoVenta(''); }}><i className="fas fa-times me-1" />Limpiar</Button></Col>}
            </Row>
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-2">
                <SortableTable maxHeight="600px" columns={[
                  { key: 'fecha', label: 'Fecha', render: v => v ? new Date(v).toLocaleDateString('es-EC') : '-' },
                  { key: 'agencia', label: 'Agencia' },
                  { key: 'nombreAsesor', label: 'Asesor' },
                  { key: 'modeloAgrupado', label: 'Modelo Agrupado' },
                  { key: 'version', label: 'Versión' },
                  { key: 'modelo', label: 'Familia' },
                  { key: 'ventaAnulacion', label: 'Venta/Anul.', render: v => <Badge bg={Number(v) > 0 ? 'success' : Number(v) < 0 ? 'danger' : 'secondary'}>{Number(v) > 0 ? `+${v}` : v}</Badge> },
                  { key: 'tipoVenta', label: 'Tipo' },
                  { key: 'baseFinanciera', label: 'Base' },
                ]} data={dashData?.detalle || []} />
              </Card.Body>
            </Card>
          </Tab>

          {/* ==================== TAB: REPORTE GERENCIAL ==================== */}
          <Tab eventKey="gerencial" title={<><i className="fas fa-briefcase me-1" />Reporte Gerencial</>}>
            {execLoading ? (
              <div className="text-center py-5"><Spinner /><p className="mt-2 text-muted">Generando reporte gerencial...</p></div>
            ) : !execReport ? (
              <div className="text-center py-4"><Button variant="primary" onClick={() => loadExecReport(selectedPeriod || dashData?.periodo)}><i className="fas fa-sync me-1" />Generar Reporte Gerencial</Button></div>
            ) : (
              <>
                <Alert variant="light" className="border shadow-sm mb-3"><i className="fas fa-file-alt me-2 text-primary" /><strong>Resumen Ejecutivo: </strong>{execReport.executiveSummary}</Alert>

                <Row className="g-3 mb-3">
                  <Col md={2}><KpiCard title="Inversión Digital" value={fmtC(execReport.funnel?.inversion)} icon="dollar-sign" /></Col>
                  <Col md={2}><KpiCard title="Impresiones" value={fmt(execReport.funnel?.impresiones)} icon="eye" color="#6BAED6" /></Col>
                  <Col md={2}><KpiCard title="Leads Digital" value={fmt(Math.round(execReport.funnel?.leadsDigital || 0))} subtitle={`CPL: ${fmtC(execReport.funnel?.cpl)}`} icon="mouse-pointer" color="#2171B5" /></Col>
                  <Col md={2}><KpiCard title="Leads CRM" value={fmt(execReport.funnel?.leadsCRM)} subtitle={`Conv CRM: ${fmtP(execReport.funnel?.tasaConversionCRM)}`} icon="user-plus" color="#00A3E0" /></Col>
                  <Col md={2}><KpiCard title="Ventas Netas" value={fmt(execReport.funnel?.ventasNetas)} subtitle={`Conv Dig: ${fmtP(execReport.funnel?.tasaConversionDigital)}`} icon="shopping-cart" color={FORD_BLUE} /></Col>
                  <Col md={2}><KpiCard title="Costo/Venta" value={fmtC(execReport.funnel?.costoVenta)} icon="calculator" color="#F26522" /></Col>
                </Row>

                <Row className="g-3 mb-3">
                  <Col md={5}>
                    <Card className="border-0 shadow-sm">
                      <Card.Header className="bg-white border-0 py-2"><strong className="small">Embudo Digital + CRM</strong></Card.Header>
                      <Card.Body className="p-2"><ReactEChartsCore echarts={echarts} option={execFunnelOpt} style={{ height: '300px' }} /></Card.Body>
                    </Card>
                  </Col>
                  <Col md={7}>
                    <Card className="border-0 shadow-sm">
                      <Card.Header className="bg-white border-0 py-2"><strong className="small">Cruce por Modelo: Ventas vs Digital</strong></Card.Header>
                      <Card.Body className="p-2"><ReactEChartsCore echarts={echarts} option={execModeloCrossOpt} style={{ height: '300px' }} /></Card.Body>
                    </Card>
                  </Col>
                </Row>

                <Card className="border-0 shadow-sm mb-3">
                  <Card.Header className="bg-white border-0 py-2"><strong className="small">Detalle por Modelo: Ventas + Métricas Digitales</strong></Card.Header>
                  <Card.Body className="p-2">
                    <SortableTable columns={[
                      { key: 'modelo', label: 'Modelo' },
                      { key: 'ventas', label: 'Ventas', render: v => <strong>{fmt(v)}</strong> },
                      { key: 'inversionDigital', label: 'Inversión', render: v => fmtC(v) },
                      { key: 'impresiones', label: 'Impresiones', render: v => fmt(v) },
                      { key: 'clicks', label: 'Clicks', render: v => fmt(v) },
                      { key: 'leadsDigital', label: 'Leads', render: v => fmt(Math.round(v || 0)) },
                      { key: 'alcance', label: 'Alcance', render: v => fmt(v) },
                      { key: 'cpl', label: 'CPL', render: v => fmtC(v) },
                      { key: 'costoVenta', label: 'Costo/Venta', render: v => fmtC(v) },
                      { key: 'tasaConversion', label: 'Conv. L→V', render: v => fmtP(v) },
                    ]} data={execReport?.byModelo || []} />
                  </Card.Body>
                </Card>

                <Card className="border-0 shadow-sm mb-3">
                  <Card.Header className="bg-white border-0 py-2"><strong className="small">Tendencia Diaria: Inversión y Leads Digitales</strong></Card.Header>
                  <Card.Body className="p-2"><ReactEChartsCore echarts={echarts} option={execDailyOpt} style={{ height: '350px' }} /></Card.Body>
                </Card>

                {execReport?.byPlatform?.length > 0 && (
                  <Card className="border-0 shadow-sm mb-3">
                    <Card.Header className="bg-white border-0 py-2"><strong className="small">Métricas por Cuenta/Plataforma Digital</strong></Card.Header>
                    <Card.Body className="p-2">
                      <SortableTable columns={[
                        { key: 'AccountName', label: 'Cuenta' },
                        { key: 'inversion', label: 'Inversión', render: v => fmtC(v) },
                        { key: 'impresiones', label: 'Impresiones', render: v => fmt(v) },
                        { key: 'clicks', label: 'Clicks', render: v => fmt(v) },
                        { key: 'leads', label: 'Leads', render: v => fmt(Math.round(v || 0)) },
                      ]} data={execReport.byPlatform} />
                    </Card.Body>
                  </Card>
                )}
              </>
            )}
          </Tab>

          {/* ==================== TAB: HISTORIAL ==================== */}
          <Tab eventKey="historial" title={<><i className="fas fa-history me-1" />Historial</>}>
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-2">
                <Table size="sm" hover>
                  <thead>
                    <tr><th>Archivo</th><th>Período</th><th>Registros</th><th>Ventas Mes</th><th>Estado</th><th>Fecha de Carga</th><th>Cargado por</th><th></th></tr>
                  </thead>
                  <tbody>
                    {uploads.map(u => (
                      <tr key={u.id}>
                        <td className="small">{u.fileName}</td>
                        <td><Badge bg="primary">{u.periodoReporte}</Badge></td>
                        <td>{fmt(u.totalRegistros)}</td>
                        <td><strong>{fmt(u.totalVentasNetas)}</strong></td>
                        <td><Badge bg={u.estado === 'COMPLETADO' ? 'success' : 'secondary'}>{u.estado}</Badge></td>
                        <td className="small">{new Date(u.fechaCarga).toLocaleString('es-EC')}</td>
                        <td className="small">{u.cargadoPor?.fullName || '-'}</td>
                        <td>{u.estado === 'COMPLETADO' && <Button size="sm" variant="outline-danger" onClick={() => handleDeleteUpload(u.id)}><i className="fas fa-trash" /></Button>}</td>
                      </tr>
                    ))}
                    {uploads.length === 0 && <tr><td colSpan={8} className="text-center text-muted py-3">Sin cargas registradas</td></tr>}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>
      )}
    </Container>
  );
};

export default FordSalesDashboard;
